import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { type CategorySelectorState } from '@/views/build/features/category-select-view';
import { isNodeEmpty } from '@/views/build/features/category-tree';
import { useInfoInitializeConfirmModal } from '../../category-select-view/components/InfoInitializeConfirmModal';
import { MobileCategorySingleSelect } from '../../category-select-view/components/MobileCategorySingleSelect';

/**
 * 과목 선택 모바일 컴포넌트
 *
 * 과목을 선택할 수 있는 SingleSelect 컴포넌트입니다.
 * 과목 변경 시 하위 학습 분야(subSubject)가 초기화되는 경우 확인 모달을 표시합니다.
 */
export const SubjectMobileSelection = ({
  categoryInfo
}: {
  categoryInfo: CategorySelectorState;
}) => {
  const {
    t
  } = useTranslation(['build', 'common']);
  const {
    openInfoInitializeConfirmModal
  } = useInfoInitializeConfirmModal();

  // 과목 선택 가능 여부
  const shouldShowSubjectSelect = useMemo(() => {
    if (categoryInfo.grade.gradeNodes?.length === 0) return true;
    if (!categoryInfo.grade.selectedGradeNode) return false;
    return !!categoryInfo.subject.subjectNodes && categoryInfo.subject.subjectNodes.length > 0;
  }, [categoryInfo.grade.gradeNodes?.length, categoryInfo.grade.selectedGradeNode, categoryInfo.subject.subjectNodes]);

  // 과목 변경 핸들러 (하위 학습 분야 초기화 확인)
  const handleValueChangeWithConfirm = useCallback(async (value: any) => {
    const hasChildSelections = !isNodeEmpty(categoryInfo.subject.selectedSubSubjectNode);
    if (hasChildSelections) {
      const confirmed = await openInfoInitializeConfirmModal();
      if (!confirmed) return;
    }
    categoryInfo.updateCategoryInfo(value);
  }, [categoryInfo, openInfoInitializeConfirmModal]);
  if (!shouldShowSubjectSelect) {
    return null;
  }
  return <MobileCategorySingleSelect value={categoryInfo.subject.selectedSubjectNode?.nodeId || null} categoryNodes={categoryInfo.subject.subjectNodes ?? []} onValueChange={handleValueChangeWithConfirm} title={t('build:category.subject')} placeholder={t('build:category.subject')} error={!!categoryInfo.errors.subject} helperText={categoryInfo.errors.subject} data-sentry-element="MobileCategorySingleSelect" data-sentry-component="SubjectMobileSelection" data-sentry-source-file="SubjectMobileSelection.tsx" />;
};